import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function Privacy() {
  return (
    <div className="min-h-screen bg-bg text-ink" data-testid="privacy-page">
      <header className="border-b border-line">
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/" className="text-sm font-bold inline-flex items-center gap-1.5 text-ink-soft hover:text-brand"><ArrowLeft className="w-4 h-4"/> Back</Link>
          <img src="/brand/petbond-logo.png" alt="PetBond" className="h-10"/>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-5 py-12 prose prose-sm">
        <h1 className="font-heading text-4xl font-extrabold">Privacy Policy</h1>
        <p className="text-ink-soft text-sm">Last updated: {new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</p>

        <h2 className="font-heading text-2xl font-extrabold mt-8">1. What we collect</h2>
        <ul>
          <li><b>Account info</b> — name, email, profile photo (from Google Sign-In)</li>
          <li><b>Pet info</b> — name, breed, age, photos, medical records you choose to add</li>
          <li><b>Service usage</b> — vet bookings, prescriptions, sponsorships, AI chat history</li>
          <li><b>Device info</b> — IP address, browser, anonymous usage analytics</li>
          <li><b>Optional</b> — phone, location (only when you submit lost-pet reports or apply to foster)</li>
        </ul>

        <h2 className="font-heading text-2xl font-extrabold mt-8">2. How we use your data</h2>
        <ul>
          <li>To deliver the core service — show your pets, book vets, run AI checks</li>
          <li>To send transactional notifications (vaccine reminders, appointment confirmations)</li>
          <li>To improve the platform — never to sell your data to third parties</li>
          <li>For verified NGO / foster matching when you opt in</li>
        </ul>

        <h2 className="font-heading text-2xl font-extrabold mt-8">3. AI conversations</h2>
        <p>Your chats with Dr. PetBond AI are stored to give you continuity across sessions. We use third-party LLM providers (Anthropic Claude, OpenAI, Google) under their data-processing terms. We do NOT use your chat data to train models. You can delete chat history anytime from your account.</p>

        <h2 className="font-heading text-2xl font-extrabold mt-8">4. Pet ID QR & lost-pet sharing</h2>
        <p>Your Pet ID QR card is <b>privacy-first</b>: scanners see only your pet's name, photo and a "Notify owner" button. Your phone number and address are NEVER displayed publicly. We forward "found pet" messages to you directly via email/WhatsApp.</p>

        <h2 className="font-heading text-2xl font-extrabold mt-8">5. Cookies & local storage</h2>
        <p>We use cookies/localStorage for session tokens, language preference, and onboarding state. No third-party advertising trackers. Analytics (if enabled) is privacy-respecting and anonymised at IP level.</p>

        <h2 className="font-heading text-2xl font-extrabold mt-8">6. Third parties we share with</h2>
        <ul>
          <li><b>Google</b> (for sign-in)</li>
          <li><b>Razorpay / Stripe</b> (for payments — they receive transaction info, not your pet data)</li>
          <li><b>Resend / Twilio</b> (for transactional email/WhatsApp delivery)</li>
          <li><b>Anthropic / OpenAI / Google</b> (for AI processing of your chat messages)</li>
          <li><b>Verified NGOs / Vets</b> (only the information you explicitly share with them — like a foster application)</li>
        </ul>

        <h2 className="font-heading text-2xl font-extrabold mt-8">7. Your rights (DPDPA 2023 + GDPR)</h2>
        <p>You have the following rights over your personal data, exercisable instantly &mdash; no support ticket required:</p>
        <ul>
          <li><b>Right to Access</b> &mdash; download a complete copy of everything we hold about you as a machine-readable JSON file.</li>
          <li><b>Right to Erasure</b> &mdash; permanently delete your account and personal data. You can choose a 30-day grace period (reversible) or immediate deletion (irreversible). Financial records are anonymised rather than deleted to satisfy the Indian Income Tax Act 6-year retention requirement.</li>
          <li><b>Right to Correct</b> &mdash; edit your name, pet info, address etc anytime from your profile.</li>
          <li><b>Right to Portability</b> &mdash; the export file uses an open JSON format you can port to another service.</li>
        </ul>
        <p>Use the self-serve page at <Link to="/app/privacy-data" className="text-brand font-bold underline">/app/privacy-data</Link> (signed-in users) or email <a href="mailto:privacy@petbond.in">privacy@petbond.in</a> &mdash; we respond within 7 working days as required under DPDP Act &sect;11.</p>

        <h2 className="font-heading text-2xl font-extrabold mt-8">8. Children</h2>
        <p>PetBond is not for users under 18. We don't knowingly collect data from children.</p>

        <h2 className="font-heading text-2xl font-extrabold mt-8">9. Security</h2>
        <p>We use HTTPS everywhere, encrypted MongoDB storage, OAuth for sign-in, and the principle of least privilege. No system is 100% secure — please use a strong unique Google password and report any concern to <a href="mailto:hello@petbond.in">hello@petbond.in</a>.</p>

        <h2 className="font-heading text-2xl font-extrabold mt-8">10. Contact</h2>
        <p>PetBond · <a href="https://www.petbond.in">www.petbond.in</a><br/>
        Email: <a href="mailto:hello@petbond.in">hello@petbond.in</a><br/>
        Phone/WhatsApp: <a href="tel:+919819793270">+91 98197 93270</a></p>
      </main>
    </div>
  );
}
